/**
 * Instrumentação local de qualidade (§18).
 *
 * A V1 **não** tem analytics remoto e nenhuma palavra digitada sai do
 * dispositivo. Este módulo só existe para desenvolvimento e testes: acumula
 * amostras em memória e expõe percentis para checar os orçamentos de §16.
 */

export type NomeMetrica =
  | 'dicionario.carregamento.ms'
  /** Até o jogo abrir com o recorte de palavras correntes (§16). */
  | 'dicionario.parcial.ms'
  | 'sugestao.latencia.ms'
  | 'sugestao.letras-sem-candidato'
  | 'posicionamento.tentativas'
  | 'cadeia.tamanho'
  | 'render.frame.ms';

/** Orçamentos de §16, usados nos testes de desempenho. */
export const ORCAMENTOS: Partial<Record<NomeMetrica, { readonly p95: number }>> = {
  'dicionario.carregamento.ms': { p95: 1500 },
  // O que o jogador espera de fato antes de poder escrever.
  'dicionario.parcial.ms': { p95: 500 },
  'sugestao.latencia.ms': { p95: 100 },
  'render.frame.ms': { p95: 16.7 },
};

const LIMITE_AMOSTRAS = 500;

const amostras = new Map<NomeMetrica, number[]>();

export function registrarMetrica(nome: NomeMetrica, valor: number): void {
  const lista = amostras.get(nome);
  if (lista === undefined) {
    amostras.set(nome, [valor]);
    return;
  }
  lista.push(valor);
  if (lista.length > LIMITE_AMOSTRAS) lista.shift();
}

export function percentil(nome: NomeMetrica, p: number): number | undefined {
  const lista = amostras.get(nome);
  if (lista === undefined || lista.length === 0) return undefined;
  const ordenada = [...lista].sort((a, b) => a - b);
  const i = Math.min(ordenada.length - 1, Math.floor((p / 100) * ordenada.length));
  return ordenada[i];
}

export function resumo(): Record<string, { amostras: number; p50?: number; p95?: number }> {
  const saida: Record<string, { amostras: number; p50?: number; p95?: number }> = {};
  for (const [nome, lista] of amostras) {
    saida[nome] = {
      amostras: lista.length,
      p50: percentil(nome, 50),
      p95: percentil(nome, 95),
    };
  }
  return saida;
}

export function limparMetricas(): void {
  amostras.clear();
}

/** Mede uma função síncrona e registra a duração. */
export function medir<T>(nome: NomeMetrica, fn: () => T, agora: () => number): T {
  const inicio = agora();
  const resultado = fn();
  registrarMetrica(nome, agora() - inicio);
  return resultado;
}
